import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { API_URL } from '../config/api';

const Reset = () => {
  // Clear any existing authentication data IMMEDIATELY when Reset page opens
  // This happens synchronously before any state initialization to ensure logout happens first
  const authKeys = [
    'id',
    'name',
    'firstName',
    'middleName',
    'lastName',
    'designationName',
    'status',
    'employmentStatus',
    'email',
    'role',
    'isReportingManager',
    'reportingManagerId',
    'reportingManagerName',
    'departmentId',
    'departmentName',
    'isAdmin',
    'isManagingDirector',
    'waitingCountdownStart'
  ];

  // Remove all authentication keys synchronously
  authKeys.forEach(key => {
    localStorage.removeItem(key);
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    temporaryPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear confirm password error when new password changes
    if (name === 'newPassword' && errors.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  };

  const validatePassword = (password) => {
    // At least 6 characters
    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.temporaryPassword.trim()) {
      newErrors.temporaryPassword = 'Temporary password is required';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setIsLoading(true);

    try {
      // Get email from URL params if available
      const email = searchParams.get('email');

      if (!email) {
        toast.error('Email is required. Please use the reset link from your email.');
        setIsLoading(false);
        return;
      }

      const payload = {
        action: 'set-employee-password',
        email: email,
        temporaryPassword: formData.temporaryPassword,
        newPassword: formData.newPassword
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        // toast.success(result.message || 'Password reset successfully');
        setIsSubmitted(true);
        // Navigate to login after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        const errorMessage = result?.message || 'Failed to reset password. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="login-wrapper">
        <div className="login-card">
          {/* Left Side - Quotes */}
          <div className="login-left">
            <div className="login-left-content">
              <div className="quote-section">
                <h1 className="quote-title1">InstaTenders HR Portal!</h1>
                <p className="quote-text">
                  காதன்மை கந்தா அறிவறியார்த் தேறுதல்
                  பேதைமை எல்லாந் தரும்.
                </p>
                <p className="quote-meaning">
                  By fond affection led who trusts in men of unwise soul,
                  Yields all his being up to folly's blind control.
                </p>
                <p className="quote-author">— திருவள்ளுவர் </p>
              </div>
            </div>
          </div>

          {/* Right Side - Reset Password Form */}
          <div className="login-right">
            <div className="login-body">
              {/* Logo Section */}
              <div className="logo-container-right">
                <img
                  src="/assets/img/Website_logo_color logo-black text-1.png"
                  alt="Company Logo"
                  className="login-logo-right"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="logo-placeholder-right" style={{ display: 'none' }}>
                  <i className="fas fa-building"></i>
                </div>
              </div>

              {!isSubmitted ? (
                <>
                  <form onSubmit={handleSubmit} noValidate>
                    {/* Temporary Password Field */}
                    <div className="form-group">
                      <label htmlFor="temporaryPassword" className="form-label">
                        Temporary Password
                      </label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <i className="fas fa-key"></i>
                        </div>
                        <input
                          type={showTemporaryPassword ? 'text' : 'password'}
                          id="temporaryPassword"
                          name="temporaryPassword"
                          className={`form-input ${errors.temporaryPassword ? 'error' : ''}`}
                          placeholder="Enter temporary password"
                          value={formData.temporaryPassword}
                          onChange={handleChange}
                          autoComplete="off"
                          aria-invalid={errors.temporaryPassword ? 'true' : 'false'}
                          aria-describedby={errors.temporaryPassword ? 'temporaryPassword-error' : undefined}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowTemporaryPassword(!showTemporaryPassword)}
                          aria-label={showTemporaryPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={showTemporaryPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </button>
                      </div>
                      {errors.temporaryPassword && (
                        <span className="error-message" id="temporaryPassword-error" role="alert">
                          <i className="fas fa-exclamation-circle"></i> {errors.temporaryPassword}
                        </span>
                      )}
                    </div>

                    {/* New Password Field */}
                    <div className="form-group">
                      <label htmlFor="newPassword" className="form-label">
                        New Password
                      </label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <i className="fas fa-lock"></i>
                        </div>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword"
                          name="newPassword"
                          className={`form-input ${errors.newPassword ? 'error' : ''}`}
                          placeholder="Enter new password"
                          value={formData.newPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          aria-invalid={errors.newPassword ? 'true' : 'false'}
                          aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </button>
                      </div>
                      {errors.newPassword && (
                        <span className="error-message" id="newPassword-error" role="alert">
                          <i className="fas fa-exclamation-circle"></i> {errors.newPassword}
                        </span>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="form-group">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm Password
                      </label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <i className="fas fa-lock"></i>
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                          placeholder="Confirm new password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          autoComplete="new-password"
                          aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                          aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <span className="error-message" id="confirmPassword-error" role="alert">
                          <i className="fas fa-exclamation-circle"></i> {errors.confirmPassword}
                        </span>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className={`login-button ${isLoading ? 'loading' : ''}`}
                      disabled={isLoading}
                    > 
                      {isLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Resetting...</span>
                        </>
                      ) : (
                        <>
                          <span>Reset Password</span>
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Login */}
                  <div className="form-options" style={{ marginTop: '24px', justifyContent: 'center' }}>
                    <Link to="/admin" className="forgot-link">
                      Back to Login
                    </Link>
                  </div>
                </>
              ) : (
                <div className="success-message">
                  <div className="success-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <h2 className="form-title">Password Reset Successful</h2>
                  <p className="form-subtitle">
                    Your password has been reset successfully.
                  </p>
                  <p className="form-subtitle" style={{ marginTop: '16px' }}>
                    Redirecting to login page...
                  </p>
                  <div className="form-options" style={{ marginTop: '32px', justifyContent: 'center' }}>
                    <Link to="/admin" className="forgot-link">
                      <i className="fas fa-arrow-left"></i> Back to Login
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reset;
